<template>
  <div>
    <div
      :key="$_sideMenu"
      v-sticky
      sticky-z-index="9"
      sticky-offset="$_offset"
    >
      <v-tabs
        v-model="activeTab"
        :mandatory="false"
        show-arrows
        background-color="grey lighten-4"
        height="36"
        class="mb-0"
      >
        <v-tab value="ar" @click="loadAging('ar')">Accounts Receivable</v-tab>
        <v-tab value="ap" @click="loadAging('ap')">Accounts Payable</v-tab>
        <v-tab value="sl" @click="loadAging('sl')">Surplus Line</v-tab>
      </v-tabs>

      <v-card
        v-if="hasLoadedAny"
        :key="`${$_sideMenu}-${activeTab}`"
        class="mb-1 px-3 d-flex justify-space-between align-center aging-summary-bar"
        height="26px"
        tile
        dark
        block
        color="primary"
      >
        <span class="pl-3 aging-summary-text">Total Records: {{ rows.length }}</span>
        <span class="pr-3 aging-summary-text">Generated: {{ reportDate }}</span>
      </v-card>
    </div>

    <v-card
      v-if="!hasLoadedAny"
      class="d-flex align-center justify-center subtitle-1 grey--text text--darken-1"
      outlined
      height="150"
    >
      Click tab to generate report
    </v-card>

    <div v-else>
      <div class="d-flex justify-end mb-1 pr-2">
        <div style="width: 50vw; max-width: 50vw; padding-right: 4px;">
          <search-input
            v-model="filter"
            class="mt-0"
            dense
            label="Search"
            :hint="searchCountLabel"
            persistent-hint
            hint-rail
            :input-props="{ appendIcon: 'mdi-magnify', singleLine: true, hideDetails: 'auto', clearable: true }"
          />
        </div>
      </div>

      <v-data-table
        :class="['elevation-1 aging-table', { 'no-actions': !showActionsColumn }]"
        dense
        :loading="loading"
        loading-text="Loading... Please wait"
        fixed-header
        disable-pagination
        hide-default-footer
        :headers="displayHeaders"
        :items="filteredRows"
        :mobile-breakpoint="0"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :style="$_cssVars"
      >
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center" style="width: 100%; gap: 2px;">
            <button-tip
              v-if="item.pid && canPolicyEdit"
              @click="$_editPolicy(item.pid)"
              icon
              iname="mdi-pencil"
              small
              tip="Edit Policy"
            />
            <button-tip
              v-if="item.fid && canFirmView"
              @click="$_viewFirmNoNotePopup(item.fid)"
              icon
              iname="mdi-open-in-new"
              small
              tip="Open Firm"
            />
          </div>
        </template>

        <template v-slot:item.amountValue="{ item }">
          <span class="text-right d-inline-block" style="width: 100%; padding-right: 8px;">{{ formatCurrency(item.amountValue) }}</span>
        </template>

        <template v-slot:item.ageSortDays="{ item }">
          <span>{{ item.ageText }}</span>
        </template>

        <template v-slot:item.note="{ item }">
          <span class="note-html" v-html="item.noteHtml"></span>
        </template>

      </v-data-table>

      <v-card class="mt-1 px-3 py-1 aging-totals-bar" tile dark block color="primary">
        <div class="aging-totals-grid">
          <div
            v-for="bucket in agingBucketSummaries"
            :key="bucket.key"
            class="aging-total-row"
          >
            <span class="aging-total-label">{{ displayBucketLabel(bucket) }}</span>
            <span class="aging-total-count">{{ bucket.filteredCount }}/{{ bucket.overallCount }}</span>
            <span class="aging-total-amount">{{ formatCurrency(bucket.filteredAmount) }} of {{ formatCurrency(bucket.overallAmount) }}</span>
          </div>
        </div>
      </v-card>
    </div>

    <ErrorDialog ref="errorDialog" />
  </div>
</template>

<script>
import ErrorDialog from "../ErrorDialog";
import currency from 'currency.js';
import { ReportsAPIService } from '@/servicehandlers/ReportsAPIService';

const reportsApiService = new ReportsAPIService();
const AGING_BUCKETS = [
  { key: 'current', label: 'Current' },
  { key: 'oneToThirty', label: '1 to 30 Days' },
  { key: 'moreThanThirty', label: 'More than 30 Days' },
  { key: 'grandTotal', label: 'Grand Total' }
];

export default {
  name: 'Aging',
  components: { ErrorDialog },
  data() {
    return {
      activeTab: 'ar',
      loading: false,
      hasLoadedAny: false,
      rows: [],
      filter: '',
      reportDate: '',
      sortBy: 'ageSortDays',
      sortDesc: true,
      headers: [
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '90px' },
        { text: 'Payee', value: 'payor', sortable: true, align: 'start', width: '90px' },
        { text: 'Insured', value: 'insured', sortable: true, align: 'start', width: '340px' },
        { text: 'Incept', value: 'incept', sortable: true, align: 'start', width: '105px' },
        { text: 'Producer', value: 'producer', sortable: true, align: 'start', width: '105px' },
        { text: 'Type', value: 'type', sortable: true, align: 'start', width: '90px' },
        { text: 'Account', value: 'acct', sortable: true, align: 'start', width: '85px' },
        { text: 'Ref', value: 'ref', sortable: true, align: 'start', width: '110px' },
        { text: 'Note', value: 'note', sortable: true, align: 'start', width: '286px' },
        { text: 'Due', value: 'dueDate', sortable: true, align: 'start', width: '105px' },
        { text: 'Age', value: 'ageSortDays', sortable: true, align: 'start', width: '110px' },
        { text: 'Amount', value: 'amountValue', sortable: true, align: 'right', width: '100px' }
      ]
    };
  },
  computed: {
    canPolicyEdit() {
      return this.$_can('policy-edit');
    },

    canFirmView() {
      return this.$_can('firm-view');
    },

    showActionsColumn() {
      // 16 Feb 2026 JCH: hide Actions column entirely when user lacks both action scopes.
      return this.canPolicyEdit || this.canFirmView;
    },

    searchCountLabel() {
      // 16 Feb 2026 JCH: x/y count must match table rows exactly.
      return `${this.filteredRows.length}/${this.rows.length}`;
    },

    filteredRows() {
      const term = this.normalizeSearchTerm(this.filter);
      if (!term) return this.rows;
      return this.rows.filter((row) => this.normalizeSearchTerm(row._searchBlob).includes(term));
    },

    agingBucketSummaries() {
      const overall = this.summarizeAgingBuckets(this.rows);
      const filtered = this.summarizeAgingBuckets(this.filteredRows);
      return AGING_BUCKETS.map((bucket) => ({
        key: bucket.key,
        label: bucket.label,
        filteredCount: filtered[bucket.key].count,
        overallCount: overall[bucket.key].count,
        filteredAmount: filtered[bucket.key].amount,
        overallAmount: overall[bucket.key].amount
      }));
    },

    displayHeaders() {
      const headers = this.headers.map((header) => ({ ...header }));
      const isAR = this.activeTab === 'ar';

      const payeeHeader = headers.find((h) => h.value === 'payor');
      if (payeeHeader && isAR) payeeHeader.text = 'Payor';

      return this.showActionsColumn ? headers : headers.filter((h) => h.value !== 'actions');
    }
  },
  created() {
    document.title = 'Aging';
    // 16 Feb 2026 JCH: load AR by default so report data is shown on entry.
    this.loadAging('ar');
  },
  methods: {
    async loadAging(agingType) {
      this.activeTab = agingType;
      this.loading = true;
      this.hasLoadedAny = true;

      try {
        const result = await reportsApiService.getAging(agingType, this.$router);
        const data = Array.isArray(result) ? result : (result && result.data ? result.data : []);

        this.rows = data.map((item) => {
          const age = item._age || {};
          const years = Number(age.years) || 0;
          const months = Number(age.months) || 0;
          const days = Number(age.days) || 0;
          const amountValue = Number(item.amt) || 0;
          const rawNote = (item.note || '').toString().replace(/\uFFFD/g, '-');
          const noteHtml = this.sanitizeNoteHtml(rawNote);
          const row = {
            ...item,
            // 16 Feb 2026 JCH: DB/business-approved mapping for AR/AP/SL: payor <- _mga, insured <- _sort.
            payor: item._mga || item.payor || item._payor || '',
            insured: item.insured || item._insured || item._sort || '',
            incept: this.formatReportDate(item._incept || item.incept || ''),
            producer: item._producer || '',
            ref: item.num || '',
            // 16 Feb 2026 JCH: keep plain text for search/sort and safe html for display.
            note: this.htmlToPlainText(noteHtml),
            noteHtml,
            dueDate: this.formatReportDate(item._due || item.due || ''),
            ageSortDays: (years * 365) + (months * 30) + days,
            ageText: this.formatAgeText(years, months, days),
            amountValue
          };
          // 16 Feb 2026 JCH: per-row search blob supports flexible matches (formatted + raw variants).
          row._searchBlob = this.buildSearchBlob(row, item);
          return row;
        });
        this.reportDate = this.formatPrintedAt(new Date());
      } catch (e) {
        this.rows = [];
        this.$refs.errorDialog.showError({ forDevelopers: e });
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(value) {
      const num = Number(value) || 0;
      const rounded = Math.round(num * 100) / 100;
      const isNegativeZero = rounded === 0 && (num < 0 || Object.is(num, -0));
      const sign = (rounded < 0 || isNegativeZero) ? '-' : '';
      const absCurrency = Math.abs(rounded).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
      return `${sign}$${absCurrency}`;
    },

    formatAgeText(years, months, days) {
      return `${years}y ${months}m ${days}d`;
    },

    formatPrintedAt(dateValue) {
      const d = new Date(dateValue);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hour24 = d.getHours();
      const minute = String(d.getMinutes()).padStart(2, '0');
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      const hour12 = hour24 % 12 || 12;
      return `${mm}-${dd}-${yyyy} ${hour12}:${minute} ${ampm}`;
    },

    formatReportDate(dateValue) {
      if (!dateValue) return '';
      const value = String(dateValue).trim();
      if (!value) return '';
      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return value;

      const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (ymdMatch) return `${ymdMatch[2]}-${ymdMatch[3]}-${ymdMatch[1]}`;

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return this.$_date(parsed);
    },

    normalizeSearchTerm(value) {
      return String(value === null || value === undefined ? '' : value).toLowerCase().replace(/\s+/g, ' ').trim();
    },

    htmlToPlainText(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return (tmp.textContent || '').trim();
    },

    sanitizeNoteHtml(rawHtml) {
      if (!rawHtml) return '';
      const container = document.createElement('div');
      container.innerHTML = rawHtml;
      this.sanitizeNoteNodeTree(container);
      return container.innerHTML;
    },

    sanitizeNoteNodeTree(root) {
      const allowedTags = new Set(['B', 'FONT']);
      const nodes = Array.from(root.querySelectorAll('*'));

      nodes.forEach((node) => {
        const tag = node.tagName;
        if (!allowedTags.has(tag)) {
          const textNode = document.createTextNode(node.textContent || '');
          if (node.parentNode) node.parentNode.replaceChild(textNode, node);
          return;
        }

        const attrs = Array.from(node.attributes || []);
        attrs.forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (tag === 'FONT' && name === 'color') {
            const normalized = this.normalizeColorValue(attr.value);
            if (normalized) node.setAttribute('color', normalized);
            else node.removeAttribute(attr.name);
            return;
          }
          node.removeAttribute(attr.name);
        });
      });
    },

    normalizeColorValue(value) {
      const v = String(value || '').trim();
      if (!v) return '';
      if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
      if (/^(?:rgb|hsl)a?\(\s*[-\d.%\s,]+\)$/.test(v)) return v;
      if (/^[a-zA-Z]+$/.test(v)) return v.toLowerCase();
      return '';
    },

    displayBucketLabel(bucket) {
      if (!(this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.xsOnly)) return bucket.label;
      const mobileMap = {
        current: 'Curr',
        oneToThirty: '1-30',
        moreThanThirty: '>30',
        grandTotal: 'Grand'
      };
      return mobileMap[bucket.key] || bucket.label;
    },

    buildSearchBlob(row, rawItem) {
      const amountDisplay = this.formatCurrency(row.amountValue);
      const amountRaw = Number(row.amountValue || 0).toFixed(2);
      const amountRawNoCommas = amountDisplay.replace(/[$,\s]/g, '');
      const inceptRaw = rawItem && (rawItem._incept || rawItem.incept || '');
      const dueRaw = rawItem && (rawItem._due || rawItem.due || '');
      return this.normalizeSearchTerm([
        row.payor, row.insured, row.incept, inceptRaw, row.producer, row.type, row.acct, row.ref, row.note,
        row.dueDate, dueRaw, row.ageText, row.ageSortDays, amountDisplay, amountRaw, amountRawNoCommas
      ].join(' | '));
    },

    summarizeAgingBuckets(rows) {
      const totals = {
        current: { count: 0, amount: currency(0) },
        oneToThirty: { count: 0, amount: currency(0) },
        moreThanThirty: { count: 0, amount: currency(0) }
      };

      rows.forEach((row) => {
        const ageDays = Number(row.ageSortDays) || 0;
        const bucketKey = this.getAgingBucketKey(ageDays);
        totals[bucketKey].count += 1;
        totals[bucketKey].amount = totals[bucketKey].amount.add(Number(row.amountValue) || 0);
      });

      return {
        current: { count: totals.current.count, amount: totals.current.amount.value },
        oneToThirty: { count: totals.oneToThirty.count, amount: totals.oneToThirty.amount.value },
        moreThanThirty: { count: totals.moreThanThirty.count, amount: totals.moreThanThirty.amount.value },
        grandTotal: {
          count: totals.current.count + totals.oneToThirty.count + totals.moreThanThirty.count,
          amount: totals.current.amount.add(totals.oneToThirty.amount).add(totals.moreThanThirty.amount).value
        }
      };
    },

    getAgingBucketKey(ageDays) {
      if (ageDays <= 0) return 'current';
      if (ageDays <= 30) return 'oneToThirty';
      return 'moreThanThirty';
    }
  }
};
</script>

<style scoped>
  .aging-summary-bar {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  .aging-table ::v-deep .v-data-table__wrapper {
    /* 16 Feb 2026 JCH: tuned to remove persistent ~20-30px bottom gap. */
    height: calc(100vh - var(--offset) - 190px) !important;
  }

  .aging-table ::v-deep td,
  .aging-table ::v-deep th {
    padding: 0 6px !important;
    font-size: 12px;
  }

  /* 16 Feb 2026 JCH: nudge Actions header up slightly to align baseline with neighboring headers. */
  .aging-table ::v-deep th.text-center span {
    position: relative;
    top: -1px;
  }

  .aging-table.no-actions ::v-deep tr > :first-child {
    padding-left: 12px !important;
  }

  .aging-total-row {
    display: grid;
    grid-template-columns: 1fr 95px 250px;
    align-items: center;
    gap: 10px;
    min-height: 22px;
    font-size: 12px;
    font-weight: 700;
  }

  .aging-totals-grid {
    width: min(100%, 560px);
    margin-left: auto;
    border-left: 1px solid rgba(255, 255, 255, 0.35);
  }

  .aging-total-row + .aging-total-row {
    border-top: 1px solid rgba(255, 255, 255, 0.28);
  }

  .aging-total-label {
    padding-left: 14px;
  }

  .aging-total-count,
  .aging-total-amount {
    text-align: right;
    font-variant-numeric: tabular-nums;
    border-left: 1px solid rgba(255, 255, 255, 0.28);
  }

  .aging-total-count {
    padding-left: 10px;
  }

  .aging-total-amount {
    padding-right: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .aging-table ::v-deep .note-html {
    display: inline;
  }

  @media (max-width: 600px) {
    .aging-summary-text {
      font-size: 11px;
      line-height: 1;
    }

    .aging-total-row {
      grid-template-columns: 56px 48px minmax(0, 1fr);
      gap: 4px;
      font-size: 9px;
    }
  }
</style>
